/* eslint-disable react-refresh/only-export-components */
import { JobId } from '@eigtech/restoration-types'
import { InspectionCorn } from '@eigtech/inspections-types'
import { ReactNode, createContext, useContext } from 'react'

export type RoutesContext = { home: string } & Partial<{
  claims: string

  claimDetail: (claimNumber: string) => string
  claimDetailCallbacks: (claimNumber: string) => string
  claimDetailContacts: (claimNumber: string) => string
  claimDetailDocuments: (claimNumber: string) => string
  claimDetailEstimates: (claimNumber: string) => string
  claimDetailEstimateDetail: (props: { claimNumber: string; estimateId: string }) => string
  claimDetailGenerateDocument: (props: { claimNumber: string }) => string
  claimDetailGenerateDocumentStatus: (props: { claimNumber: string; templateId: string }) => string
  claimDetailGenerateDocumentGuided: (props: {
    claimNumber: string
    guidedDocumentType: string
  }) => string
  claimDetailInfo: (claimNumber: string) => string
  claimDetailInspections: (claimNumber: string) => string
  claimDetailInspectionDetail: (props: {
    claimNumber: string
    inspectionId: InspectionCorn
  }) => string
  claimDetailMeetings: (claimNumber: string) => string
  claimDetailNewMeeting: (claimNumber: string) => string
  claimDetailMeeting: (props: {
    claimNumber: string
    meetingStartTime: string
    meetingId: string
  }) => string
  claimDetailNotes: (claimNumber: string) => string
  claimDetailNewNote: (claimNumber: string) => string
  claimDetailMedia: (claimNumber: string) => string
  claimDetailPortals: (claimNumber: string) => string
  claimDetailPhotoReport: (claimNumber: string) => string
  claimDetailPhotoReportStatus: (claimNumber: string) => string
  claimDetailUploadDocument: (claimNumber: string) => string

  jobs: string

  jobDetail: (jobId: JobId) => string
  jobDetailAppointments: (jobId: JobId) => string
  jobDetailCallbacks: (jobId: JobId) => string
  jobDetailContacts: (jobId: JobId) => string
  jobDetailDocuments: (jobId: JobId) => string
  jobDetailGenerateDocument: (props: { jobId: JobId }) => string
  jobDetailGenerateDocumentStatus: (props: { jobId: JobId; templateId: string }) => string
  jobDetailInfo: (jobId: JobId) => string
  jobDetailInspections: (jobId: JobId) => string
  jobDetailInspectionDetail: (props: { jobId: JobId; inspectionId: InspectionCorn }) => string
  jobDetailMeeting: (props: { jobId: JobId; meetingStartTime: string; meetingId: string }) => string
  jobDetailMeetings: (jobId: JobId) => string
  jobDetailNewMeeting: (jobId: JobId) => string
  jobDetailNotes: (jobId: JobId) => string
  jobDetailNewNote: (jobId: JobId) => string
  jobDetailMedia: (jobId: JobId) => string
  jobDetailPortals: (jobId: JobId) => string
  jobDetailTimelineNewEvent: (jobId: JobId) => string
  jobDetailPhotoReport: (jobId: JobId) => string
  jobDetailPhotoReportStatus: (jobId: JobId) => string
  jobDetailUploadDocument: (jobId: JobId) => string
  jobDetailBilling: (jobId: JobId) => string
  jobDetailBillDetail: (props: { jobId: JobId; billId: string }) => string
  jobDetailInvoiceDetail: (props: { jobId: JobId; invoiceId: string }) => string

  inbox: string
  inboxMessage: (options: { messageId: string }) => string

  activeCallbacks: string

  documentation: string

  templates: string

  vendorRates: string
  vendorRatesDetail: (props: { vendorRatesId: string }) => string
}>

const RoutesContext = createContext<RoutesContext>({ home: '/' })

export function RoutesProvider({
  children,
  routes,
}: {
  children: ReactNode
  routes: RoutesContext
}) {
  return <RoutesContext.Provider value={routes}>{children}</RoutesContext.Provider>
}

export function useRoutesContext() {
  const routes = useContext(RoutesContext)

  if (!routes) {
    throw new Error('RoutesProvider required to use useRoutesContext')
  }

  return routes
}
